import { useMemo, useState } from 'react'
import { Box, Input, NativeSelect, Text, VStack } from '@chakra-ui/react';
import { useDroppable } from '@dnd-kit/core'
import { meetingCounts } from './lobbyScheduleUtils'
import { AttendeePoolCard } from './AttendeePoolCard'
import type { ScheduleAttendeeDto, ScheduleMeetingDto } from '../../../types/lobbyDay'

type SortMode = 'name' | 'load'

interface AttendeePoolProps {
  attendees: ScheduleAttendeeDto[]
  meetings: ScheduleMeetingDto[]
  onSelectAttendee?: (attendeeId: string) => void
  /** The pool attendee currently being dragged (dims the others); also a drop target for removal. */
  activeAttendeeId?: string | null
  /** Attendees to tint green — the constituents of a currently-hovered legislator. */
  highlightAttendeeIds?: Set<string> | null
  /** Reports which attendee card the pointer is over (or null on leave); drives the reverse hover glow. */
  onHoverAttendee?: (attendeeId: string | null) => void
}

/** Left column: the pool of attendees available to assign to meetings. Droppable target —
 *  dragging an assignment chip here removes it from its meeting. */
export function AttendeePool({
  attendees, meetings, onSelectAttendee, activeAttendeeId, highlightAttendeeIds, onHoverAttendee,
}: AttendeePoolProps) {
  const [search, setSearch] = useState('')
  const [sort, setSort] = useState<SortMode>('name')
  const { setNodeRef } = useDroppable({ id: 'pool', data: { type: 'pool' } })

  const counts = useMemo(() => meetingCounts(meetings), [meetings])

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    const list = q ? attendees.filter((a) => a.fullName.toLowerCase().includes(q)) : [...attendees]
    return list.sort((a, b) =>
      sort === 'name'
        ? a.fullName.localeCompare(b.fullName)
        : (counts.get(b.attendeeId) ?? 0) - (counts.get(a.attendeeId) ?? 0))
  }, [attendees, search, sort, counts])

  return (
    <Box ref={setNodeRef} w="260px" flexShrink={0}>
      <VStack align="stretch" gap={2} mb={3}>
        <Input size="sm" placeholder="Search attendees…" value={search} onChange={(e) => setSearch(e.target.value)} />
        <NativeSelect.Root size="sm">
          <NativeSelect.Field
            value={sort}
            onChange={(e) => setSort(e.target.value as SortMode)}>
            <option value="name">Sort: Name</option>
            <option value="load">Sort: Load</option>
          </NativeSelect.Field>
          <NativeSelect.Indicator />
        </NativeSelect.Root>
      </VStack>
      <Box overflowY="auto" maxH="70vh" pr={1}>
        {filtered.map((a) => (
          <Box
            key={a.attendeeId}
            onClick={() => onSelectAttendee?.(a.attendeeId)}
            onMouseEnter={() => onHoverAttendee?.(a.attendeeId)}
            onMouseLeave={() => onHoverAttendee?.(null)}
            cursor={onSelectAttendee ? 'pointer' : 'default'}
          >
            <AttendeePoolCard
              attendee={a}
              count={counts.get(a.attendeeId) ?? 0}
              dimmed={!!activeAttendeeId && activeAttendeeId !== a.attendeeId}
              highlighted={!!highlightAttendeeIds?.has(a.attendeeId)}
            />
          </Box>
        ))}
        {filtered.length === 0 && (
          <Text fontSize="sm" color="gray.500" textAlign="center" py={6}>No attendees match.</Text>
        )}
      </Box>
    </Box>
  );
}
